import React, { useMemo, useState, useEffect } from "react";
import { supabase } from "../Services/supabaseClient";
import { useCompany } from "../context/CompanyContext";
import { v4 as uuidv4 } from "uuid";
import { Link } from "react-router-dom";
import "./TeamSettings.css";
import {
  Users2,
  MailPlus,
  Shield,
  UserPlus,
  AtSign,
  Loader2,
  Trash2,
  CheckCircle2,
  Save,
} from "lucide-react";

const BUSINESS_ROLE_OPTIONS = [
  {
    value: "superadmin",
    label: "SuperAdmin",
    access:
      "Full access to everything. Can manage billing, team settings, setup, roles, payroll, CRM, accounting, inventory, reports and all modules.",
  },
  {
    value: "manager",
    label: "Manager",
    access: "Can access almost everything except Team Settings and Billing.",
  },
  {
    value: "accountant",
    label: "Accountant",
    access:
      "Can access finance, accounting, reports and business modules except Setup, Team Settings and Billing.",
  },
  {
    value: "hr",
    label: "Human Resource",
    access:
      "Payroll Management only. User is redirected to Payroll after login.",
  },
  {
    value: "marketing",
    label: "Marketing",
    access: "CRM only. User is redirected to CRM after login.",
  },
  {
    value: "operations",
    label: "Operations",
    access:
      "Dashboard, customers, vendors, quick sales, quick purchases, bookings, budgeting, inventory, fixed assets, AR/AP, estimates, invoices, receipts, basic reports, account ledger and inventory valuation. Cannot create journals.",
  },
  {
    value: "reviewer",
    label: "Reviewer",
    access: "Dashboard, inventory dashboard and reports only.",
  },
];

function normalizeEmail(v) {
  return String(v || "")
    .trim()
    .toLowerCase();
}

function isExpired(expiresAt) {
  if (!expiresAt) return false;
  const exp = new Date(expiresAt).getTime();
  return Number.isFinite(exp) && exp <= Date.now();
}

function getBusinessRoleLabel(value) {
  return (
    BUSINESS_ROLE_OPTIONS.find((r) => r.value === value)?.label || "Unassigned"
  );
}

export default function TeamSettings() {
  const {
    company,
    companyId,
    members = [],
    setMembers,
    plan,
    refreshCompanyContext,
  } = useCompany();

  const isBusinessPlan = plan === "business";

  const [businessRole, setBusinessRole] = useState("operations");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [loading, setLoading] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [me, setMe] = useState(null);
  const [profilesById, setProfilesById] = useState({});
  const [savingRoleFor, setSavingRoleFor] = useState(null);

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase.auth.getUser();
      if (!error) setMe(data?.user || null);
    })();
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (company?.company_name) {
        if (!cancelled) setCompanyName(company.company_name);
        return;
      }

      if (!companyId) return;

      const { data: comp, error: compErr } = await supabase
        .from("companies")
        .select("id, company_name, name, owner_id")
        .eq("id", companyId)
        .maybeSingle();

      if (!cancelled && !compErr && (comp?.company_name || comp?.name)) {
        setCompanyName(comp.company_name || comp.name);
        return;
      }

      const ownerId = comp?.owner_id || company?.owner_id;

      if (ownerId) {
        const { data: ci } = await supabase
          .from("company_info")
          .select("company_name")
          .eq("user_id", ownerId)
          .maybeSingle();

        if (!cancelled && ci?.company_name) setCompanyName(ci.company_name);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [company, companyId]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const ids = Array.from(
          new Set((members || []).map((m) => m.user_id).filter(Boolean))
        );

        if (!ids.length) {
          if (!cancelled) setProfilesById({});
          return;
        }

        const { data, error } = await supabase
          .from("profiles")
          .select("id, first_name, last_name, email")
          .in("id", ids);

        if (error) throw error;

        const map = {};
        (data || []).forEach((p) => {
          map[p.id] = p;
        });

        if (!cancelled) setProfilesById(map);
      } catch (e) {
        console.error("Failed to fetch profiles:", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [members]);

  const totalAdmins = useMemo(
    () => members.filter((m) => m.role === "admin").length,
    [members]
  );

  const totalMembers = useMemo(
    () => members.filter((m) => m.role !== "admin").length,
    [members]
  );

  const totalSuperAdmins = useMemo(
    () => members.filter((m) => m.business_role === "superadmin").length,
    [members]
  );

  async function sendInviteEmailResend({
    to_email,
    invite_token,
    company_name,
    expires_at,
  }) {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    if (!token) throw new Error("Not authenticated.");

    const baseUrl =
      process.env.REACT_APP_SUPABASE_URL || process.env.VITE_SUPABASE_URL;

    if (!baseUrl) throw new Error("Missing Supabase URL env var.");

    const fnUrl = `${baseUrl}/functions/v1/send-invite-email`;

    const payload = {
      to_email,
      invite_token,
      company_name,
      expires_at,
      app_url: window.location.origin,
    };

    const res = await fetch(fnUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    const raw = await res.text();
    let json;

    try {
      json = raw ? JSON.parse(raw) : {};
    } catch {
      json = { error: raw || "Unknown error" };
    }

    if (!res.ok) {
      throw new Error(json?.error || `Invite email failed (${res.status})`);
    }

    return json;
  }

  const sendInvite = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!companyId) throw new Error("Missing companyId.");

      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError || !user) {
        throw new Error("You must be signed in to send invites.");
      }

      const cleanEmail = normalizeEmail(email);
      if (!cleanEmail.includes("@")) throw new Error("Enter a valid email.");

      const token = uuidv4();
      const expiresAt = new Date(Date.now() + 7 * 24 * 3600_000).toISOString();

      const { data: existingInvite } = await supabase
        .from("company_invites")
        .select("token, expires_at, accepted")
        .eq("company_id", companyId)
        .eq("invited_email", cleanEmail)
        .eq("accepted", false)
        .order("expires_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (existingInvite?.token && !isExpired(existingInvite.expires_at)) {
        throw new Error(
          "An active invite already exists for this email. Ask them to check their email or wait for it to expire."
        );
      }

      const { error: insertError } = await supabase
        .from("company_invites")
        .insert([
          {
            token,
            company_id: companyId,
            invited_email: cleanEmail,
            invited_role: isBusinessPlan ? "member" : role,
            invited_business_role: isBusinessPlan ? businessRole : null,
            invited_by: user.id,
            expires_at: expiresAt,
            accepted: false,
            company_name: companyName || null,
          },
        ]);

      if (insertError) throw insertError;

      try {
        await sendInviteEmailResend({
          to_email: cleanEmail,
          invite_token: token,
          company_name: companyName || "Your Company",
          expires_at: expiresAt,
        });
      } catch (mailErr) {
        console.error("Resend invite email error:", mailErr);
      }

      alert("Invite sent!");
      setEmail("");
      setRole("member");
      setBusinessRole("operations");
    } catch (err) {
      alert(err?.message || "Error sending invite.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateMemberBusinessRole = async (member, nextBusinessRole) => {
    if (!companyId || !member?.user_id) return;

    const targetIsMe = me?.id && member.user_id === me.id;
    const targetIsSuperAdmin = member.business_role === "superadmin";
    const demotingLastSuperAdmin =
      targetIsSuperAdmin &&
      nextBusinessRole !== "superadmin" &&
      totalSuperAdmins <= 1;

    if (targetIsMe && nextBusinessRole !== "superadmin") {
      alert("You cannot remove your own SuperAdmin access.");
      return;
    }

    if (demotingLastSuperAdmin) {
      alert("You cannot remove the last SuperAdmin.");
      return;
    }

    setSavingRoleFor(member.user_id);

    try {
      const { error } = await supabase
        .from("company_members")
        .update({
          role: nextBusinessRole === "superadmin" ? "admin" : "member",
          business_role: nextBusinessRole,
        })
        .eq("company_id", companyId)
        .eq("user_id", member.user_id);

      if (error) throw error;

      setMembers(
        members.map((m) =>
          m.user_id === member.user_id
            ? {
                ...m,
                role: nextBusinessRole === "superadmin" ? "admin" : "member",
                business_role: nextBusinessRole,
              }
            : m
        )
      );

      if (typeof refreshCompanyContext === "function") {
        refreshCompanyContext();
      }
    } catch (err) {
      console.error("Could not update role:", err);
      alert(err?.message || "Could not update member role.");
    } finally {
      setSavingRoleFor(null);
    }
  };

  const updateLegacyRole = async (member, nextRole) => {
    if (!companyId || !member?.user_id) return;

    const isTargetAdmin = member.role === "admin";
    const isLastAdmin =
      isTargetAdmin && nextRole !== "admin" && totalAdmins <= 1;

    if (me?.id && member.user_id === me.id && nextRole !== "admin") {
      alert("You cannot remove your own admin access.");
      return;
    }

    if (isLastAdmin) {
      alert("You cannot remove the last admin.");
      return;
    }

    setSavingRoleFor(member.user_id);

    try {
      const { error } = await supabase
        .from("company_members")
        .update({ role: nextRole })
        .eq("company_id", companyId)
        .eq("user_id", member.user_id);

      if (error) throw error;

      setMembers(
        members.map((m) =>
          m.user_id === member.user_id ? { ...m, role: nextRole } : m
        )
      );

      if (typeof refreshCompanyContext === "function") {
        refreshCompanyContext();
      }
    } catch (err) {
      console.error("Could not update role:", err);
      alert(err?.message || "Could not update member role.");
    } finally {
      setSavingRoleFor(null);
    }
  };

  const removeMember = async (uid) => {
    if (!companyId) return;

    const target = members.find((m) => m.user_id === uid);
    const isTargetAdmin = target?.role === "admin";
    const isTargetSuperAdmin = target?.business_role === "superadmin";

    if (me?.id && uid === me.id) {
      alert("You cannot remove yourself.");
      return;
    }

    if (!isBusinessPlan && isTargetAdmin && totalAdmins <= 1) {
      alert("You cannot remove the last admin.");
      return;
    }

    if (isBusinessPlan && isTargetSuperAdmin && totalSuperAdmins <= 1) {
      alert("You cannot remove the last SuperAdmin.");
      return;
    }

    if (!window.confirm("Remove this member?")) return;

    const { error } = await supabase
      .from("company_members")
      .delete()
      .match({ company_id: companyId, user_id: uid });

    if (error) {
      alert("Could not remove member.");
      console.error(error);
    } else {
      setMembers(members.filter((m) => m.user_id !== uid));
    }
  };

  const displayLabel = (m) => {
    const prof = m?.user_id ? profilesById[m.user_id] : null;

    const fullName =
      prof && (prof.first_name || prof.last_name)
        ? `${prof.first_name || ""} ${prof.last_name || ""}`.trim()
        : "";

    const emailish =
      prof?.email || m.invited_email || m.email || m.user_email || "";

    if (fullName && emailish) return `${fullName} • ${emailish}`;
    if (emailish) return emailish;
    if (fullName) return fullName;

    return m.user_id || "Unknown user";
  };

  const secondaryLabel = (m) => {
    return m.user_id ? `ID: ${m.user_id}` : "";
  };

  return (
    <div className="team-shell">
      <header className="team-head panel">
        <div className="title">
          <h2>
            <Users2 size={18} /> Team Settings
          </h2>
          <p className="muted">
            Manage collaborators for{" "}
            <strong>{companyName || "Your Company"}</strong>
          </p>
        </div>

        <div className="stats">
          <span className="chip">
            <Shield size={14} /> Admins: <strong>{totalAdmins}</strong>
          </span>
          {isBusinessPlan && (
            <span className="chip">
              <Shield size={14} /> SuperAdmins:{" "}
              <strong>{totalSuperAdmins}</strong>
            </span>
          )}
          <span className="chip">
            <Users2 size={14} /> Members: <strong>{totalMembers}</strong>
          </span>
          <span className="chip ok">
            <CheckCircle2 size={14} /> Total: <strong>{members.length}</strong>
          </span>
        </div>
      </header>

      {isBusinessPlan && (
        <section className="panel members-card">
          <div className="members-head">
            <h3>
              <Shield size={16} /> Business Role Breakdown
            </h3>
            <p className="muted">
              Use these roles to control what each team member can access.
            </p>
          </div>

          <div className="table-wrap">
            <table className="members-table">
              <thead>
                <tr>
                  <th>Role</th>
                  <th>Access / Responsibility</th>
                </tr>
              </thead>
              <tbody>
                {BUSINESS_ROLE_OPTIONS.map((r) => (
                  <tr key={r.value}>
                    <td>
                      <span
                        className={`role-badge ${r.business_role || r.role}`}
                      >
                        {r.label}{" "}
                      </span>
                    </td>
                    <td>{r.access}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="panel invite-card">
        <div className="invite-head">
          <h3>
            <MailPlus size={16} /> Invite a New Member
          </h3>
          <p className="muted">
            Send a 7-day invite to join your workspace with a selected role.
          </p>
        </div>

        <form onSubmit={sendInvite} className="invite-form">
          <label className="stack">
            Email address
            <div className="input-icon">
              <AtSign size={14} />
              <input
                type="email"
                placeholder="person@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                required
              />
            </div>
          </label>

          {isBusinessPlan ? (
            <label className="stack">
              Business Role
              <select
                className="member-role-select"
                value={businessRole}
                onChange={(e) => setBusinessRole(e.target.value)}
                disabled={loading}
              >
                {BUSINESS_ROLE_OPTIONS.filter(
                  (r) => r.value !== "superadmin"
                ).map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <label className="stack">
              Role
              <select
                className="member-role-select"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                disabled={loading}
              >
                <option value="admin">Admin</option>
                <option value="member">Member</option>
              </select>
            </label>
          )}

          <div className="invite-actions">
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="spin" size={16} /> Sending…
                </>
              ) : (
                <>
                  <UserPlus size={16} /> Send Invite
                </>
              )}
            </button>
          </div>
        </form>
      </section>

      <section className="panel members-card">
        <div className="members-head">
          <h3>
            <Users2 size={16} /> Current Members
          </h3>
          <p className="muted">
            Manage user roles. Changes take effect immediately for active users.
          </p>
        </div>

        <div className="table-wrap">
          <table className="members-table">
            <thead>
              <tr>
                <th>User / Email</th>
                <th>Role</th>
                <th className="center">Actions</th>
              </tr>
            </thead>

            <tbody>
              {members.map((m) => {
                const label = displayLabel(m);
                const initials = (label || "?").charAt(0).toUpperCase();
                const saving = savingRoleFor === m.user_id;

                return (
                  <tr key={`${m.user_id}-${m.role}-${m.business_role || ""}`}>
                    <td className="user-cell">
                      <div className="avatar">{initials}</div>
                      <div className="uinfo">
                        <div className="uname">{label}</div>
                        <div className="uid">{secondaryLabel(m)}</div>
                      </div>
                    </td>

                    <td>
                      {isBusinessPlan ? (
                        <select
                          className="member-role-select"
                          value={m.business_role || "operations"}
                          disabled={saving}
                          onChange={(e) =>
                            updateMemberBusinessRole(m, e.target.value)
                          }
                        >
                          {BUSINESS_ROLE_OPTIONS.map((r) => (
                            <option key={r.value} value={r.value}>
                              {r.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <select
                          className="member-role-select"
                          value={m.role || "member"}
                          disabled={saving}
                          onChange={(e) => updateLegacyRole(m, e.target.value)}
                        >
                          <option value="admin">Admin</option>
                          <option value="member">Member</option>
                        </select>
                      )}

                      {isBusinessPlan && (
                        <div className="uid">
                          Current: {getBusinessRoleLabel(m.business_role)}
                        </div>
                      )}
                    </td>

                    <td className="center">
                      {saving ? (
                        <span className="chip">
                          <Save size={14} /> Saving
                        </span>
                      ) : (
                        <button
                          className="icon-btn danger"
                          onClick={() => removeMember(m.user_id)}
                          title="Remove"
                          aria-label={`Remove ${label}`}
                          disabled={loading}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}

              {!members.length && (
                <tr>
                  <td colSpan="3">
                    <div className="empty-state">
                      No team members yet. Send an invite above to get started.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <div className="help-row">
        <Link to="/settings" className="btn-ghost">
          Back to Setup
        </Link>
      </div>
    </div>
  );
}
